import {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Select, Space, Table, Tag, Typography} from "antd";
import {ClipboardList} from "lucide-react";
import {Navigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {API, APIv1} from "../../api";
import {extractDateBySecond} from "../../utils";
import "./AuditLogs.scss";

const {Text} = Typography;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const ACTION_COLOR_MAP = {
    create: "green",
    update: "blue",
    delete: "red",
};

const ACTION_VALUE_MAP = {
    0: "create",
    1: "update",
    2: "delete",
    create: "create",
    update: "update",
    delete: "delete",
};

const getPositiveParam = (searchParams, key, fallback) => {
    const value = Number(searchParams.get(key));
    return Number.isFinite(value) && value > 0 ? value : fallback;
};

const parseChanges = (changes) => {
    if (!changes) {
        return null;
    }

    if (typeof changes !== "string") {
        return changes;
    }

    try {
        return JSON.parse(changes);
    } catch (err) {
        return changes;
    }
};

const getActionType = (action) => ACTION_VALUE_MAP[String(action).toLowerCase()] || String(action || "").toLowerCase();

const getActionFilterValue = (action) => {
    const normalizedAction = getActionType(action);

    if (normalizedAction === "create") return "0";
    if (normalizedAction === "update") return "1";
    if (normalizedAction === "delete") return "2";

    return action;
};

const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
};

const formatContentType = (contentType) => {
    if (!contentType) {
        return "-";
    }

    return String(contentType).split(".").pop();
};

const getResponseList = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    return [];
};

const renderChangesPreview = (changes, t) => {
    if (!changes) {
        return <Text type="secondary">{t("common.notAvailable")}</Text>;
    }

    if (typeof changes === "string") {
        return <Text code ellipsis={{tooltip: changes}}>{changes}</Text>;
    }

    const keys = Object.keys(changes);

    if (!keys.length) {
        return <Text type="secondary">{t("common.notAvailable")}</Text>;
    }

    return (
        <Space size={4} wrap>
            {keys.slice(0, 3).map((key) => (
                <Tag key={key}>{key}</Tag>
            ))}
            {keys.length > 3 && <Tag>+{keys.length - 3}</Tag>}
        </Space>
    );
};

const renderExpandedChanges = (record, t) => {
    const changes = record.changes;

    if (!changes) {
        return <Text type="secondary">{t("pages.auditLogs.emptyChanges")}</Text>;
    }

    if (typeof changes === "string") {
        return <pre className="audit-logs__pre">{changes}</pre>;
    }

    return (
        <div className="audit-logs__changes">
            {Object.entries(changes).map(([field, value]) => {
                const isPair = Array.isArray(value) && value.length >= 2;

                return (
                    <div className="audit-logs__change-row" key={field}>
                        <Text strong>{field}</Text>
                        {isPair ? (
                            <div className="audit-logs__change-values">
                                <Text className="audit-logs__change-value audit-logs__change-value--from">
                                    {formatChangeValue(value[0])}
                                </Text>
                                <span className="audit-logs__change-arrow">-&gt;</span>
                                <Text className="audit-logs__change-value audit-logs__change-value--to">
                                    {formatChangeValue(value[1])}
                                </Text>
                            </div>
                        ) : (
                            <Text>{formatChangeValue(value)}</Text>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const AuditLogs = () => {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [userData, setUserData] = useState({});
    const [auditLogs, setAuditLogs] = useState([]);
    const [actors, setActors] = useState([]);
    const [contentTypes, setContentTypes] = useState([]);
    const [totalLogs, setTotalLogs] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [filters, setFilters] = useState({
        content_type_id: searchParams.get("content_type_id") || undefined,
        actor_id: searchParams.get("actor_id") || undefined,
        action: searchParams.get("action") || undefined,
    });
    const isStaff = Boolean(userData?.data?.is_admin);

    const currentPage = getPositiveParam(searchParams, "page", DEFAULT_PAGE);
    const pageSize = getPositiveParam(searchParams, "page_size", DEFAULT_PAGE_SIZE);
    const ordering = searchParams.get("ordering") || "";

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem("user"));

        if (items) {
            setUserData(items);
        }
    }, []);

    useEffect(() => {
        setFilters({
            content_type_id: searchParams.get("content_type_id") || undefined,
            actor_id: searchParams.get("actor_id") || undefined,
            action: searchParams.get("action") || undefined,
        });
    }, [searchParams]);

    const fetchFilterOptions = useCallback(async () => {
        if (!userData.token || !isStaff) {
            return;
        }

        setFiltersLoading(true);

        try {
            const headers = {
                Authorization: `Token ${userData.token}`,
            };
            const [actorsResponse, contentTypesResponse] = await Promise.allSettled([
                API.get("/api/v1/audit-logs/actors/", {headers}),
                APIv1.get("/audit-logs/content-types/", {headers}),
            ]);

            if (actorsResponse.status === "fulfilled") {
                setActors(getResponseList(actorsResponse.value.data));
            } else {
                console.error("Failed to load audit log actors:", actorsResponse.reason);
                setActors([]);
            }

            if (contentTypesResponse.status === "fulfilled") {
                setContentTypes(getResponseList(contentTypesResponse.value.data));
            } else {
                console.error("Failed to load audit log content types:", contentTypesResponse.reason);
                setContentTypes([]);
            }
        } catch (err) {
            console.error("Something went wrong:", err);
        } finally {
            setFiltersLoading(false);
        }
    }, [isStaff, userData.token]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    const actionOptions = useMemo(() => ([
        {label: t("pages.auditLogs.actions.create"), value: "0"},
        {label: t("pages.auditLogs.actions.update"), value: "1"},
        {label: t("pages.auditLogs.actions.delete"), value: "2"},
    ]), [t]);

    const actorOptions = useMemo(() => actors.filter((actor) => actor?.id !== undefined).map((actor) => ({
        label: actor.username || `User ${actor.id}`,
        value: String(actor.id),
    })), [actors]);

    const contentTypeOptions = useMemo(() => contentTypes.filter((contentType) => contentType?.id !== undefined).map((contentType) => ({
        label: contentType.model || formatContentType(contentType.label),
        value: String(contentType.id),
    })), [contentTypes]);

    const columns = useMemo(() => [
        {
            title: t("pages.auditLogs.columns.id"),
            dataIndex: "id",
            width: 80,
        },
        {
            title: t("pages.auditLogs.columns.action"),
            dataIndex: "action",
            filters: actionOptions,
            filteredValue: filters.action ? [filters.action] : null,
            filterMultiple: false,
            render: (action) => {
                const actionType = getActionType(action);

                return (
                    <Tag color={ACTION_COLOR_MAP[actionType] || "default"}>
                        {t(`pages.auditLogs.actions.${actionType}`, actionType || "-")}
                    </Tag>
                );
            },
        },
        {
            title: t("pages.auditLogs.columns.object"),
            dataIndex: "objectRepr",
            render: (text, record) => (
                <div className="audit-logs__object">
                    <Text>{text || "-"}</Text>
                    <Text type="secondary">pk: {record.objectPk || "-"}</Text>
                </div>
            ),
        },
        {
            title: t("pages.auditLogs.columns.contentType"),
            dataIndex: "contentType",
        },
        {
            title: t("pages.auditLogs.columns.actor"),
            dataIndex: "actor",
        },
        {
            title: t("pages.auditLogs.columns.remoteAddr"),
            dataIndex: "remoteAddr",
            render: (text) => text || "-",
        },
        {
            title: t("pages.auditLogs.columns.changes"),
            dataIndex: "changes",
            render: (changes) => renderChangesPreview(changes, t),
        },
        {
            title: t("pages.auditLogs.columns.timestamp"),
            dataIndex: "timestamp",
            sorter: true,
            sortOrder: ordering === "timestamp" ? "ascend" : ordering === "-timestamp" ? "descend" : null,
        },
    ], [actionOptions, filters.action, ordering, t]);

    const fetchAuditLogs = useCallback(async () => {
        if (!userData.token || !isStaff) {
            return;
        }

        setLoading(true);

        try {
            const params = {
                page: currentPage,
                page_size: pageSize,
            };

            ["content_type_id", "actor_id", "action", "ordering"].forEach((key) => {
                const value = searchParams.get(key);

                if (value) {
                    params[key] = value;
                }
            });

            const response = await APIv1.get("/audit-logs/list/", {
                params,
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });
            const results = Array.isArray(response.data?.results)
                ? response.data.results
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            const data = results.map((item) => ({
                key: item.id,
                id: item.id,
                objectPk: item.object_pk,
                objectRepr: item.object_repr,
                action: item.action,
                changes: parseChanges(item.changes),
                remoteAddr: item.remote_addr,
                timestamp: extractDateBySecond(item.timestamp),
                additionalData: item.additional_data,
                contentType: formatContentType(item.content_type),
                actor: item.actor,
            }));

            setAuditLogs(data);
            setTotalLogs(response.data?.count ?? data.length);
        } catch (err) {
            console.error("Something went wrong:", err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, isStaff, pageSize, searchParams, userData.token]);

    useEffect(() => {
        fetchAuditLogs();
    }, [fetchAuditLogs]);

    const updateParams = (updates = {}, resetKeys = []) => {
        const nextParams = new URLSearchParams(searchParams);

        resetKeys.forEach((key) => nextParams.delete(key));

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                nextParams.delete(key);
            } else {
                nextParams.set(key, String(value));
            }
        });

        setSearchParams(nextParams, {replace: true});
    };

    const handleFilterChange = (key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    const applyFilters = () => {
        updateParams({
            page: 1,
            content_type_id: filters.content_type_id,
            actor_id: filters.actor_id,
            action: filters.action,
        }, ["object_pk", "content_type"]);
    };

    const clearFilters = () => {
        setFilters({
            content_type_id: undefined,
            actor_id: undefined,
            action: undefined,
        });
        updateParams({page: 1}, ["object_pk", "content_type", "content_type_id", "actor_id", "action"]);
    };

    const handleTableChange = (pagination, tableFilters, sorter, extra = {}) => {
        if (extra.action === "paginate") {
            updateParams({
                page: pagination.current,
                page_size: pagination.pageSize,
            });
            return;
        }

        const selectedAction = Object.prototype.hasOwnProperty.call(tableFilters, "action")
            ? tableFilters.action?.[0]
            : filters.action;

        updateParams({
            page: 1,
            action: selectedAction ? getActionFilterValue(selectedAction) : null,
            ordering: sorter?.order ? `${sorter.order === "descend" ? "-" : ""}timestamp` : null,
        });
    };

    if (userData.token && !isStaff) {
        return <Navigate to="/analysis" replace/>;
    }

    return (
        <div className="content_container audit-logs">
            <div className="audit-logs__header">
                <div>
                    <h1>{t("pages.auditLogs.title")}</h1>
                    <p>{t("pages.auditLogs.subtitle")}</p>
                </div>
                <ClipboardList size={32} color="#6cb4fc"/>
            </div>

            <div className="audit-logs__filters">
                <Select
                    allowClear
                    showSearch
                    placeholder={t("pages.auditLogs.filters.contentTypeId")}
                    loading={filtersLoading}
                    options={contentTypeOptions}
                    optionFilterProp="label"
                    value={filters.content_type_id}
                    onChange={(value) => handleFilterChange("content_type_id", value)}
                />
                <Select
                    allowClear
                    showSearch
                    placeholder={t("pages.auditLogs.filters.actorId")}
                    loading={filtersLoading}
                    options={actorOptions}
                    optionFilterProp="label"
                    value={filters.actor_id}
                    onChange={(value) => handleFilterChange("actor_id", value)}
                />
                <Select
                    allowClear
                    placeholder={t("pages.auditLogs.filters.action")}
                    options={actionOptions}
                    value={filters.action}
                    onChange={(value) => handleFilterChange("action", value)}
                />
                <Space>
                    <Button type="primary" onClick={applyFilters}>{t("pages.auditLogs.filters.apply")}</Button>
                    <Button onClick={clearFilters}>{t("common.clearBtn")}</Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={auditLogs}
                loading={loading}
                onChange={handleTableChange}
                expandable={{
                    expandedRowRender: (record) => renderExpandedChanges(record, t),
                    rowExpandable: (record) => Boolean(record.changes),
                }}
                pagination={{
                    total: totalLogs,
                    current: currentPage,
                    pageSize,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                    pageSizeOptions: ["10", "20", "50", "100"],
                }}
            />
        </div>
    );
};

export default AuditLogs;
